/**
 * Store Zustand para Carta Porte
 * Sprint D — HU-D3
 */

import { create } from 'zustand';
import { cartaPorteService } from '../services/cartaPorte';
import type {
    FiscalData,
    CsdInfo,
    TimbreProfile,
    CartaPorteItem,
    CartaPorteDetail,
    PreflightResult,
    StampIngresoDryRunResult,
    StampIngresoReadiness,
    StampIngresoRequest,
    StampIngresoResult,
} from '../types/cartaPorte';

interface CartaPorteState {
    // Datos fiscales
    fiscalData: FiscalData | null;
    fiscalLoading: boolean;
    fiscalError: string | null;

    // CSD
    csds: CsdInfo[];
    csdLoading: boolean;
    csdError: string | null;

    // Timbres
    timbreProfile: TimbreProfile | null;
    timbreLoading: boolean;

    // Cartas porte
    cartasPorte: CartaPorteItem[];
    cartasPorteLoading: boolean;
    cartasPorteMeta: { current_page: number; last_page: number; per_page: number; total: number } | null;
    selectedCartaPorte: CartaPorteDetail | null;
    selectedLoading: boolean;
    stampIngresoReadinessLoading: boolean;
    stampIngresoSubmitting: boolean;
    stampIngresoError: string | null;

    // Acciones — Fiscal
    fetchFiscalData: () => Promise<void>;
    saveFiscalData: (data: FiscalData) => Promise<void>;

    // Acciones — CSD
    fetchCsds: () => Promise<void>;
    uploadCsd: (formData: FormData) => Promise<void>;

    // Acciones — Timbres
    fetchTimbreProfile: () => Promise<void>;

    // Acciones — Cartas porte
    fetchCartasPorte: (params?: { page?: number; status?: string }) => Promise<void>;
    fetchCartaPorteDetail: (id: number) => Promise<void>;
    createFromContract: (contractId: number) => Promise<CartaPorteItem>;

    // Acciones — Timbrado
    preflight: (id: number) => Promise<PreflightResult>;
    stamp: (id: number) => Promise<CartaPorteItem>;
    fetchStampIngresoReadiness: (contractId: number) => Promise<StampIngresoReadiness>;
    dryRunStampIngreso: (id: number, payload: StampIngresoRequest) => Promise<StampIngresoDryRunResult>;
    stampIngreso: (id: number, payload: StampIngresoRequest) => Promise<{
        cartaPorte: CartaPorteItem;
        stampResult: StampIngresoResult;
    }>;

    // Reset
    reset: () => void;
}

const initialState = {
    fiscalData: null,
    fiscalLoading: false,
    fiscalError: null,
    csds: [],
    csdLoading: false,
    csdError: null,
    timbreProfile: null,
    timbreLoading: false,
    cartasPorte: [],
    cartasPorteLoading: false,
    cartasPorteMeta: null,
    selectedCartaPorte: null,
    selectedLoading: false,
    stampIngresoReadinessLoading: false,
    stampIngresoSubmitting: false,
    stampIngresoError: null,
};

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'message' in error) {
        return String((error as { message: string }).message);
    }
    return 'Error inesperado';
}

export const useCartaPorteStore = create<CartaPorteState>((set, get) => ({
    ...initialState,

    // ── Datos Fiscales ──

    fetchFiscalData: async () => {
        set({ fiscalLoading: true, fiscalError: null });
        try {
            const data = await cartaPorteService.getFiscalData();
            set({ fiscalData: data, fiscalLoading: false });
        } catch (error) {
            set({ fiscalLoading: false, fiscalError: extractErrorMessage(error) });
        }
    },

    saveFiscalData: async (data: FiscalData) => {
        set({ fiscalLoading: true, fiscalError: null });
        try {
            const updated = await cartaPorteService.setFiscalData(data);
            set({ fiscalData: updated, fiscalLoading: false });
        } catch (error) {
            set({ fiscalLoading: false, fiscalError: extractErrorMessage(error) });
            throw error;
        }
    },

    // ── CSD ──

    fetchCsds: async () => {
        set({ csdLoading: true, csdError: null });
        try {
            const data = await cartaPorteService.listCsds();
            set({ csds: data, csdLoading: false });
        } catch (error) {
            set({ csdLoading: false, csdError: extractErrorMessage(error) });
        }
    },

    uploadCsd: async (formData: FormData) => {
        set({ csdLoading: true, csdError: null });
        try {
            const newCsd = await cartaPorteService.uploadCsd(formData);
            set((state) => ({
                csds: [newCsd, ...state.csds],
                csdLoading: false,
            }));
        } catch (error) {
            set({ csdLoading: false, csdError: extractErrorMessage(error) });
            throw error;
        }
    },

    // ── Timbres ──

    fetchTimbreProfile: async () => {
        set({ timbreLoading: true });
        try {
            const data = await cartaPorteService.getTimbreProfile();
            set({ timbreProfile: data, timbreLoading: false });
        } catch {
            set({ timbreLoading: false });
        }
    },

    // ── Cartas Porte ──

    fetchCartasPorte: async (params) => {
        set({ cartasPorteLoading: true });
        try {
            const { items, meta } = await cartaPorteService.listCartasPorte({
                page: params?.page,
                status: params?.status,
                limit: 20,
            });
            set({
                cartasPorte: params?.page && params.page > 1
                    ? [...get().cartasPorte, ...items]
                    : items,
                cartasPorteMeta: meta ?? null,
                cartasPorteLoading: false,
            });
        } catch {
            set({ cartasPorteLoading: false });
        }
    },

    fetchCartaPorteDetail: async (id: number) => {
        set({ selectedLoading: true });
        try {
            const detail = await cartaPorteService.getCartaPorteDetail(id);
            set({ selectedCartaPorte: detail, selectedLoading: false });
        } catch {
            set({ selectedLoading: false });
        }
    },

    createFromContract: async (contractId: number) => {
        const item = await cartaPorteService.createFromContract(contractId);
        set((state) => ({
            cartasPorte: [item, ...state.cartasPorte],
        }));
        return item;
    },

    // ── Timbrado ──

    preflight: async (id: number) => {
        return cartaPorteService.preflightStamp(id);
    },

    stamp: async (id: number) => {
        const updated = await cartaPorteService.stampCartaPorte(id);
        set((state) => ({
            cartasPorte: state.cartasPorte.map((cp) =>
                cp.id === updated.id ? updated : cp,
            ),
            selectedCartaPorte: state.selectedCartaPorte?.id === updated.id
                ? { ...state.selectedCartaPorte, ...updated }
                : state.selectedCartaPorte,
        }));
        return updated;
    },

    fetchStampIngresoReadiness: async (contractId: number) => {
        set({ stampIngresoReadinessLoading: true, stampIngresoError: null });
        try {
            const readiness = await cartaPorteService.getStampIngresoReadiness(contractId);
            set({ stampIngresoReadinessLoading: false });
            return readiness;
        } catch (error) {
            const message = extractErrorMessage(error);
            set({ stampIngresoReadinessLoading: false, stampIngresoError: message });
            throw error;
        }
    },

    dryRunStampIngreso: async (id: number, payload: StampIngresoRequest) => {
        set({ stampIngresoSubmitting: true, stampIngresoError: null });
        try {
            const result = await cartaPorteService.dryRunStampIngreso(id, payload);
            set((state) => ({
                stampIngresoSubmitting: false,
                selectedCartaPorte: state.selectedCartaPorte?.id === id
                    ? {
                        ...state.selectedCartaPorte,
                        stamp_ingreso_dry_run_response: result,
                        stamp_ingreso_request: payload,
                        stamp_ingreso_trace_id: result.trace_id ?? state.selectedCartaPorte.stamp_ingreso_trace_id ?? null,
                    }
                    : state.selectedCartaPorte,
            }));
            return result;
        } catch (error) {
            const message = extractErrorMessage(error);
            set({ stampIngresoSubmitting: false, stampIngresoError: message });
            throw error;
        }
    },

    stampIngreso: async (id: number, payload: StampIngresoRequest) => {
        set({ stampIngresoSubmitting: true, stampIngresoError: null });
        try {
            const result = await cartaPorteService.stampIngreso(id, payload);
            set((state) => ({
                cartasPorte: state.cartasPorte.map((cp) =>
                    cp.id === result.cartaPorte.id ? result.cartaPorte : cp,
                ),
                selectedCartaPorte: state.selectedCartaPorte?.id === result.cartaPorte.id
                    ? { ...state.selectedCartaPorte, ...result.cartaPorte }
                    : state.selectedCartaPorte,
                stampIngresoSubmitting: false,
            }));
            return result;
        } catch (error) {
            const message = extractErrorMessage(error);
            set({ stampIngresoSubmitting: false, stampIngresoError: message });
            throw error;
        }
    },

    // ── Reset ──

    reset: () => set(initialState),
}));
